import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * SpotlightOverlay - Highlights a specific element on the page
 *
 * Creates a dark overlay with a "spotlight" cutout around the target element.
 * Used during onboarding to draw attention to specific UI elements.
 */
const SpotlightOverlay = ({
  targetSelector,
  targetRef,
  padding = 8,
  borderRadius = 8,
  onClickOutside,
  onClickTarget,
  children,
  position = 'bottom', // 'top', 'bottom', 'left', 'right'
  showPulse = true,
}) => {
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Get target element
  const getTargetElement = useCallback(() => {
    if (targetRef?.current) {
      return targetRef.current;
    }
    if (targetSelector) {
      return document.querySelector(targetSelector);
    }
    return null;
  }, [targetSelector, targetRef]);

  // Update target position
  const updatePosition = useCallback(() => {
    const target = getTargetElement();
    if (!target) {
      setTargetRect(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    setTargetRect({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Calculate tooltip position
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 320;
    const tooltipHeight = 150;
    const gap = 16;

    let top, left;

    switch (position) {
      case 'top':
        top = rect.top - tooltipHeight - gap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - gap;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + gap;
        break;
      default:
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    // Keep tooltip in viewport
    if (left < 16) left = 16;
    if (left + tooltipWidth > viewportWidth - 16) left = viewportWidth - tooltipWidth - 16;
    if (top < 16) top = rect.bottom + gap;
    if (top + tooltipHeight > viewportHeight - 16) top = rect.top - tooltipHeight - gap;

    setTooltipPosition({ top, left });
  }, [getTargetElement, padding, position]);

  // Update on mount and resize
  useEffect(() => {
    updatePosition();

    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    // Observe DOM changes
    const observer = new MutationObserver(updatePosition);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      observer.disconnect();
    };
  }, [updatePosition]);

  // Handle clicks
  const handleOverlayClick = (e) => {
    if (!targetRect) {
      onClickOutside?.();
      return;
    }

    const x = e.clientX;
    const y = e.clientY;

    // Check if click is within spotlight
    if (
      x >= targetRect.left &&
      x <= targetRect.left + targetRect.width &&
      y >= targetRect.top &&
      y <= targetRect.top + targetRect.height
    ) {
      onClickTarget?.();
    } else {
      onClickOutside?.();
    }
  };

  if (!targetRect) {
    return null;
  }

  const overlayContent = (
    <>
      {/* Dark overlay with cutout */}
      <div
        className="fixed inset-0 z-[100] pointer-events-auto"
        role="button"
        tabIndex={0}
        onClick={handleOverlayClick}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') && handleOverlayClick()}
        aria-label="Close spotlight"
        style={{
          background: `
            linear-gradient(to bottom, rgba(0,0,0,0.75), rgba(0,0,0,0.75)) no-repeat,
            linear-gradient(to bottom, rgba(0,0,0,0.75), rgba(0,0,0,0.75)) no-repeat,
            linear-gradient(to bottom, rgba(0,0,0,0.75), rgba(0,0,0,0.75)) no-repeat,
            linear-gradient(to bottom, rgba(0,0,0,0.75), rgba(0,0,0,0.75)) no-repeat
          `,
          backgroundSize: `
            100% ${targetRect.top}px,
            100% calc(100% - ${targetRect.top + targetRect.height}px),
            ${targetRect.left}px ${targetRect.height}px,
            calc(100% - ${targetRect.left + targetRect.width}px) ${targetRect.height}px
          `,
          backgroundPosition: `
            0 0,
            0 ${targetRect.top + targetRect.height}px,
            0 ${targetRect.top}px,
            ${targetRect.left + targetRect.width}px ${targetRect.top}px
          `,
        }}
      />

      {/* Spotlight border and pulse */}
      <div
        className="fixed z-[101] pointer-events-none"
        style={{
          top: targetRect.top,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
          borderRadius: borderRadius,
        }}
      >
        {/* Border glow */}
        <div
          className="absolute inset-0 border-2 border-blue-400"
          style={{ borderRadius: borderRadius }}
        />

        {/* Pulse animation */}
        {showPulse && (
          <div
            className="absolute inset-0 animate-ping border-2 border-blue-400 opacity-75"
            style={{
              borderRadius: borderRadius,
              animationDuration: '2s',
            }}
          />
        )}
      </div>

      {/* Tooltip content */}
      {children && (
        <div
          className="fixed z-[102] w-80 p-4 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
          }}
        >
          {children}
        </div>
      )}
    </>
  );

  return createPortal(overlayContent, document.body);
};

export default SpotlightOverlay;
